/**
 * LA LISTA de pasos del alta — una sola, para las tres vistas que la enseñan.
 *
 * La auditoría del onboarding del 2026-10-29 (hallazgo 4) encontró tres listas
 * que no coincidían: el asistente (3 pasos), «Tareas pendientes» de Inicio (5)
 * y Configuración › Primeros pasos («3 de 6», con 7 filas y sin la
 * autorización ni el segundo factor). Cada una calculaba su estado por su
 * cuenta, así que lo que una daba por hecho la otra lo pedía.
 *
 * ```text
 * AQUI      que pasos hay, en que orden, y como esta cada uno
 * ASISTENTE los tres de en medio (fiscal, autorizacion, mfa), con ESTE estado
 * TAREAS    los que estan pendientes o en espera y tienen algo que hacer
 * PRIMEROS  todos, con su estado y su boton
 * PASOS
 * ```
 *
 * Y el estado es el de `OD-0039` E-14: «hecho» sólo cuando ese paso ya no
 * impide facturar. Lo que está hecho por la persona y todavía no vale (datos
 * sin confirmar, autorización en comprobación) es `espera`.
 *
 * PURO: sin red, sin React y sin textos. Los textos los pone cada vista desde
 * `i18n`, y por eso un test puede importar esto tal cual.
 */

/** Estado real del emisor (GET /api/v1/legal-entity), o `null` mientras no se sabe. */
export type EstadoEmisor = "sin-datos" | "sin-confirmar" | "confirmado" | null;

/** `OD-0035`: la respuesta del SII. `null` = sin responder; `undefined` = no se sabe. */
export type RespuestaSii = "NOT_SII" | "SII" | null | undefined;

export type EstadoPasoAlta = "hecho" | "espera" | "pendiente" | "automatico" | "desconocido" | "fuera";

export type IdPasoAlta = "cuenta" | "email" | "organizacion" | "fiscal" | "autorizacion" | "mfa" | "serie" | "cliente" | "factura";

export type PasoAlta = { readonly id: IdPasoAlta; readonly estado: EstadoPasoAlta };

/** Lo que hace falta saber para calcularla. `null` en cualquiera = no se sabe. */
export type DatosDelAlta = {
  readonly emisor: EstadoEmisor;
  readonly sii: RespuestaSii;
  /** El estado de la autorización ante la AEAT, tal cual lo da la API. */
  readonly autorizacion: string | null;
  readonly mfaActivo: boolean | null;
  readonly tieneClientes: boolean | null;
  readonly tieneFacturaEmitida: boolean | null;
};

/**
 * Los datos fiscales. Hechos SOLO confirmados y con el SII respondido «No»:
 * es lo que mira el control previo de la emisión (`OD-0015` D-03, `OD-0035`).
 * Con «Sí» quedan `fuera`: no hay nada que hacer que deje facturar.
 */
export function estadoFiscal(emisor: EstadoEmisor, sii: RespuestaSii): EstadoPasoAlta {
  if (emisor === null) return "desconocido";
  if (sii === "SII") return "fuera";
  if (emisor === "sin-datos") return "pendiente";
  if (emisor === "confirmado" && sii === "NOT_SII") return "hecho";
  // Confirmados sin saber el SII no se afirma nada: puede estar respondido.
  if (emisor === "confirmado" && sii === undefined) return "desconocido";
  return "espera";
}

/**
 * La autorización: hecha ACTIVA; en comprobación (`PENDING_VALIDATION`), en
 * espera. Lo demás —sin empezar, a medias, rechazada, retirada— pendiente.
 */
export function estadoAutorizacion(estado: string | null): EstadoPasoAlta {
  if (estado === null) return "desconocido";
  if (estado === "ACTIVE") return "hecho";
  if (estado === "PENDING_VALIDATION") return "espera";
  return "pendiente";
}

export function estadoMfa(activo: boolean | null): EstadoPasoAlta {
  return activo === null ? "desconocido" : activo ? "hecho" : "pendiente";
}

function siNo(valor: boolean | null): EstadoPasoAlta {
  return valor === null ? "desconocido" : valor ? "hecho" : "pendiente";
}

/**
 * Los pasos de `OD-0015` D-02 más la autorización (`OD-0031`) y el segundo
 * factor (`OD-0039` E-09), en el orden del recorrido.
 *
 * ```text
 * cuenta, email, organizacion   hechos por definicion: sin ellos no hay panel
 * serie                         automatica: la crea la primera emision
 * ```
 */
export function pasosDelAlta(d: DatosDelAlta): readonly PasoAlta[] {
  return [
    { id: "cuenta", estado: "hecho" },
    { id: "email", estado: "hecho" },
    { id: "organizacion", estado: "hecho" },
    { id: "fiscal", estado: estadoFiscal(d.emisor, d.sii) },
    { id: "autorizacion", estado: estadoAutorizacion(d.autorizacion) },
    { id: "mfa", estado: estadoMfa(d.mfaActivo) },
    { id: "serie", estado: "automatico" },
    { id: "cliente", estado: siNo(d.tieneClientes) },
    { id: "factura", estado: siNo(d.tieneFacturaEmitida) },
  ];
}

/** Los pasos que van DENTRO del asistente, en su orden. */
export const PASOS_DEL_ASISTENTE: readonly IdPasoAlta[] = ["fiscal", "autorizacion", "mfa"];

/**
 * Los pasos que son una tarea: los que esperan algo de la persona. `espera`
 * entra —«confirma tus datos», «la autorización se está comprobando»— porque
 * todavía no deja facturar; `desconocido`, no: lo que no se sabe no se pide.
 */
export function pasosPorHacer(pasos: readonly PasoAlta[]): readonly PasoAlta[] {
  return pasos.filter((p) => p.estado === "pendiente" || p.estado === "espera");
}

/** Hechos y total, sin el automático: un progreso que no puede llegar al final es un reproche. */
export function progresoDelAlta(pasos: readonly PasoAlta[]): { readonly hechos: number; readonly total: number } {
  const contables = pasos.filter((p) => p.estado !== "automatico" && p.estado !== "fuera");
  return { hechos: contables.filter((p) => p.estado === "hecho").length, total: contables.length };
}
